import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  TextField, 
  IconButton,
  Typography,
  Box,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

function ChatDialog({ 
  isOpen, 
  onClose, 
  socket,
  routeId, 
  driverName = 'Driver'
}) {
  const [messageInput, setMessageInput] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedHistory, setHasLoadedHistory] = useState(false);
  const messagesEndRef = useRef(null);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatHistory]);

  // Load chat history when dialog opens
  const loadChatHistory = useCallback(() => {
    if (!socket || !routeId) {
      console.log('ChatDialog: Cannot load history - no socket or routeId');
      return;
    }

    setIsLoading(true);
    
    socket.emit('chat:getMessages', { routeId: routeId }, (response) => {
      setIsLoading(false);
      
      // Response is an array of messages directly
      if (Array.isArray(response)) {
        const formattedMessages = response.map(msg => ({
          id: msg.id || `${msg.timestamp}-${msg.from}`,
          text: msg.message || msg.text || msg.data,
          timestamp: msg.timestamp,
          // Messages from dispatch to driver (direction 'Out') are received by driver
          type: msg.direction === 'Out' ? 'received' : 'sent',
          from: msg.direction === 'Out' ? (msg.from || 'Dispatch') : driverName
        }));
        
        setChatHistory(formattedMessages.sort((a, b) => 
          new Date(a.timestamp) - new Date(b.timestamp)
        ));
        setHasLoadedHistory(true);
      } else {
        console.log('ChatDialog: No messages or error:', response?.error);
        setHasLoadedHistory(true);
      }
    });
  }, [socket, routeId, driverName]);

  // Load history when dialog opens
  useEffect(() => {
    if (isOpen && !hasLoadedHistory) {
      loadChatHistory();
    }
  }, [isOpen, hasLoadedHistory, loadChatHistory]);

  // Reset loaded state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setHasLoadedHistory(false);
    }
  }, [isOpen]);

  // Set up real-time message listening
  useEffect(() => {
    if (!isOpen || !socket) return;

    const handleNewMessage = (messageData) => {
      // Only process messages for this route
      if (messageData.routeId !== routeId) {
        return;
      }

      const newMessage = {
        id: messageData.id || `${messageData.timestamp}-${messageData.from}`,
        text: messageData.message || messageData.data,
        timestamp: messageData.timestamp,
        type: messageData.direction === 'Out' ? 'received' : 'sent',
        from: messageData.direction === 'Out' ? (messageData.from || 'Dispatch') : driverName
      };

      setChatHistory(prevHistory => {
        // Avoid duplicates
        const exists = prevHistory.some(msg => 
          msg.id === newMessage.id || 
          (msg.text === newMessage.text && 
           Math.abs(new Date(msg.timestamp) - new Date(newMessage.timestamp)) < 1000)
        );
        
        if (!exists) {
          return [...prevHistory, newMessage].sort((a, b) => 
            new Date(a.timestamp) - new Date(b.timestamp)
          );
        }
        return prevHistory;
      });
    };

    socket.on('chat:newMessage', handleNewMessage);

    return () => {
      socket.off('chat:newMessage', handleNewMessage);
    };
  }, [isOpen, socket, routeId, driverName]);

  const handleSendMessage = () => {
    if (!messageInput.trim() || !socket || !routeId) return;

    const messageData = {
      routeId: routeId,
      message: messageInput.trim(),
      from: driverName,
      direction: 'In' // Message coming from driver to dispatch
    };

    socket.emit('driver:sendMessage', messageData, (response) => {
      if (response?.success) {
        // Add message to local history immediately
        const newMessage = {
          id: `${Date.now()}-${driverName}`,
          text: messageInput.trim(),
          timestamp: new Date().toISOString(),
          type: 'sent',
          from: driverName
        };
        
        setChatHistory(prevHistory => [...prevHistory, newMessage]);
        setMessageInput('');
      } else {
        console.error('ChatDialog: Failed to send message:', response?.error);
      }
    });
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const formatTime = (timestamp) => {
    try {
      const date = new Date(timestamp);
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '';
    }
  };

  return (
    <Dialog 
      open={isOpen} 
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: { height: '80vh', maxHeight: '600px' }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Chat with Dispatch</Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ color: 'grey.500' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', p: 2 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flex: 1, overflowY: 'auto', mb: 2 }}>
            {chatHistory.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ mt: 4 }}>
                No messages yet. Start a conversation with dispatch.
              </Typography>
            ) : (
              chatHistory.map((msg) => (
                <Box
                  key={msg.id}
                  sx={{
                    display: 'flex',
                    justifyContent: msg.type === 'sent' ? 'flex-end' : 'flex-start',
                    mb: 1
                  }}
                >
                  <Box
                    sx={{
                      maxWidth: '75%',
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: msg.type === 'sent' ? 'primary.main' : 'grey.200',
                      color: msg.type === 'sent' ? 'white' : 'text.primary'
                    }}
                  >
                    <Typography variant="caption" display="block" sx={{ 
                      fontWeight: 'bold',
                      color: msg.type === 'sent' ? 'rgba(255,255,255,0.8)' : 'text.secondary'
                    }}>
                      {msg.from}
                    </Typography>
                    <Typography variant="body2">{msg.text}</Typography>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block',
                        textAlign: 'right',
                        mt: 0.5,
                        color: msg.type === 'sent' ? 'rgba(255,255,255,0.7)' : 'text.secondary'
                      }}
                    >
                      {formatTime(msg.timestamp)}
                    </Typography>
                  </Box>
                </Box>
              ))
            )}
            <div ref={messagesEndRef} />
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ p: 2, gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Type a message..."
          value={messageInput}
          onChange={(e) => setMessageInput(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={!socket || !routeId}
        />
        <Button 
          variant="contained" 
          onClick={handleSendMessage}
          disabled={!messageInput.trim() || !socket || !routeId}
          sx={{ minWidth: 'auto', px: 2 }}
        >
          <SendIcon />
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ChatDialog;
